import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Search, X, Mail, Phone, Building2, Clock, MessageSquare,
  Check, CheckCheck, Trash2, Inbox, TrendingUp, Ban, Sparkles, MoreVertical,
  UserPlus, Copy, ExternalLink, Plus
} from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

const STATUS_META = {
  new:       { label_es: "Nuevo",       label_en: "New",       color: "#2563EB", bg: "#DBEAFE", Icon: Sparkles },
  contacted: { label_es: "Contactado",  label_en: "Contacted", color: "#B45309", bg: "#FEF3C7", Icon: CheckCheck },
  converted: { label_es: "Convertido",  label_en: "Converted", color: "#166534", bg: "#DCFCE7", Icon: TrendingUp },
  discarded: { label_es: "Descartado",  label_en: "Discarded", color: "#64748B", bg: "#F1F5F9", Icon: Ban },
};

function fmtDateTime(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "es" ? "es-ES" : "en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function GestionLeads() {
  const { lang, t } = useLang();
  const nav = useNavigate();
  const isES = lang === "es";
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ new: 0, contacted: 0, converted: 0, discarded: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [convertResult, setConvertResult] = useState(null);
  const [newLeadOpen, setNewLeadOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [lr, sr] = await Promise.all([api.get("/admin/leads"), api.get("/admin/leads/summary")]);
      setItems(lr.data);
      setSummary(sr.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((l) => {
      if (filter !== "all" && (l.status || "new") !== filter) return false;
      if (!q) return true;
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.company || "").toLowerCase().includes(q) ||
        (l.message || "").toLowerCase().includes(q)
      );
    });
  }, [items, filter, query]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, { status });
      setItems((prev) => prev.map((l) => (l.id === id ? data : l)));
      // Refresh summary
      const sr = await api.get("/admin/leads/summary");
      setSummary(sr.data);
    } catch (e) { alert(fmtErr(e)); }
  };

  const removeLead = async (id) => {
    if (!window.confirm(isES ? "¿Eliminar este lead permanentemente?" : "Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setItems((prev) => prev.filter((l) => l.id !== id));
      if (openId === id) setOpenId(null);
      const sr = await api.get("/admin/leads/summary");
      setSummary(sr.data);
    } catch (e) { alert(fmtErr(e)); }
  };

  const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.new;
    const Icon = meta.Icon;
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
            style={{ background: meta.bg, color: meta.color }}
            data-testid={`lead-status-badge-${status}`}>
        <Icon size={11} strokeWidth={2.5} />
        {isES ? meta.label_es : meta.label_en}
      </span>
    );
  };

  const TabBtn = ({ value, count, Icon, label }) => (
    <button
      onClick={() => setFilter(value)}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-bold transition-all ${
        filter === value
          ? "bg-[var(--ink)] text-white shadow-sm"
          : "bg-white text-slate-600 border border-[var(--border)] hover:bg-slate-50"
      }`}
      data-testid={`leads-filter-${value}`}
    >
      <Icon size={14} strokeWidth={2.4} />
      {label}
      <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-black ${
        filter === value ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
      }`}>{count}</span>
    </button>
  );

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto pb-24" data-testid="page-leads">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full bg-white border border-[var(--border)] flex items-center justify-center lg:hidden">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="sns-title text-2xl">{isES ? "Leads recibidos" : "Received leads"}</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {isES
              ? "Solicitudes de contacto desde tu landing pública /pro."
              : "Contact requests from your public /pro landing."}
          </p>
        </div>
        <button
          onClick={() => setNewLeadOpen(true)}
          className="sns-btn sns-btn-accent px-4 py-2.5 text-sm inline-flex items-center gap-2 shrink-0"
          data-testid="new-lead-button"
        >
          <Plus size={16} /> {isES ? "Nuevo lead" : "New lead"}
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { key: "new", value: summary.new, color: "#2563EB", bg: "#DBEAFE", Icon: Sparkles, label_es: "Nuevos", label_en: "New" },
          { key: "contacted", value: summary.contacted, color: "#B45309", bg: "#FEF3C7", Icon: CheckCheck, label_es: "Contactados", label_en: "Contacted" },
          { key: "converted", value: summary.converted, color: "#166534", bg: "#DCFCE7", Icon: TrendingUp, label_es: "Convertidos", label_en: "Converted" },
          { key: "discarded", value: summary.discarded, color: "#64748B", bg: "#F1F5F9", Icon: Ban, label_es: "Descartados", label_en: "Discarded" },
        ].map((s) => (
          <div key={s.key} className="sns-card p-4" data-testid={`leads-stat-${s.key}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: s.bg, color: s.color }}>
                <s.Icon size={18} strokeWidth={2.4} />
              </div>
              <div>
                <div className="text-2xl font-black leading-none" style={{ color: s.color }}>{s.value}</div>
                <div className="text-xs text-slate-500 mt-1 font-semibold">{isES ? s.label_es : s.label_en}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters + search */}
      <div className="sns-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2">
          <TabBtn value="all" count={summary.total} Icon={Inbox} label={isES ? "Todos" : "All"} />
          <TabBtn value="new" count={summary.new} Icon={Sparkles} label={isES ? "Nuevos" : "New"} />
          <TabBtn value="contacted" count={summary.contacted} Icon={CheckCheck} label={isES ? "Contactados" : "Contacted"} />
          <TabBtn value="converted" count={summary.converted} Icon={TrendingUp} label={isES ? "Convertidos" : "Converted"} />
          <TabBtn value="discarded" count={summary.discarded} Icon={Ban} label={isES ? "Descartados" : "Discarded"} />
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={isES ? "Buscar por nombre, email, empresa..." : "Search by name, email, company..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="sns-input pl-9 py-2 text-sm"
            data-testid="leads-search"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">{isES ? "Cargando..." : "Loading..."}</div>
      ) : filtered.length === 0 ? (
        <div className="sns-card p-10 text-center" data-testid="leads-empty">
          <Inbox size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-semibold">
            {items.length === 0
              ? (isES ? "Aún no has recibido ningún lead." : "You haven't received any leads yet.")
              : (isES ? "No hay leads con estos filtros." : "No leads match these filters.")}
          </p>
          {items.length === 0 && (
            <p className="text-slate-400 text-sm mt-2">
              {isES ? "Cuando alguien rellene el formulario en /pro, aparecerá aquí." : "When someone fills the /pro form, they'll appear here."}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => {
            const isOpen = openId === lead.id;
            const status = lead.status || "new";
            return (
              <div
                key={lead.id}
                className={`sns-card p-4 md:p-5 transition-all ${isOpen ? "ring-2 ring-[var(--coral)]/30" : ""}`}
                data-testid={`lead-card-${lead.id}`}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center font-bold text-slate-500 text-sm shrink-0">
                    {(lead.name || "?").substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-800 truncate" data-testid={`lead-name-${lead.id}`}>{lead.name}</h3>
                      <StatusBadge status={status} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                      <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-[var(--coral)]">
                        <Mail size={12} /> {lead.email}
                      </a>
                      {lead.company && (
                        <span className="inline-flex items-center gap-1"><Building2 size={12} /> {lead.company}</span>
                      )}
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-[var(--coral)]">
                          <Phone size={12} /> {lead.phone}
                        </a>
                      )}
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmtDateTime(lead.received_at, lang)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpenId(isOpen ? null : lead.id)}
                    className="text-slate-400 hover:text-slate-600 shrink-0"
                    data-testid={`lead-toggle-${lead.id}`}
                    aria-label={isOpen ? "Cerrar" : "Abrir"}
                  >
                    <MoreVertical size={18} />
                  </button>
                </div>

                {lead.message && (
                  <div className={`mt-3 text-sm text-slate-600 whitespace-pre-wrap ${isOpen ? "" : "line-clamp-2"}`}>
                    <MessageSquare size={12} className="inline mr-1 -mt-0.5 text-slate-400" />
                    {lead.message}
                  </div>
                )}

                {isOpen && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-2" data-testid={`lead-actions-${lead.id}`}>
                    <button
                      onClick={async () => {
                        if (status === "converted") return;
                        const tenantName = (lead.company || lead.name || "").trim();
                        if (!tenantName || !lead.email) {
                          alert(isES ? "Faltan datos del lead (nombre/empresa y email)" : "Missing lead data (name/company and email)");
                          return;
                        }
                        if (!window.confirm(isES
                            ? `¿Crear cliente para "${tenantName}" en plan Free y enviarle credenciales por email?`
                            : `Create customer "${tenantName}" on Free plan and email credentials?`)) return;
                        try {
                          const { data } = await api.post(`/admin/leads/${lead.id}/convert`, {
                            tenant_name: tenantName,
                            admin_name: lead.name || tenantName,
                            admin_email: lead.email,
                            plan: "free",
                            lang: isES ? "es" : "en",
                          });
                          setConvertResult(data);
                          await reload();
                        } catch (ex) { alert(fmtErr(ex)); }
                      }}
                      disabled={status === "converted"}
                      className="sns-btn bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`lead-accept-quick-${lead.id}`}
                    >
                      <Check size={14} /> {isES ? "Aceptar rápido" : "Quick accept"}
                    </button>
                    <button
                      onClick={() => setConvertLead(lead)}
                      disabled={status === "converted"}
                      className="sns-btn sns-btn-accent px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`lead-convert-${lead.id}`}
                    >
                      <UserPlus size={14} /> {isES ? "Convertir con opciones" : "Convert with options"}
                    </button>
                    <a
                      href={`mailto:${lead.email}?subject=${encodeURIComponent(isES ? "Re: tu solicitud en Smile & Shine" : "Re: your request at Smile & Shine")}`}
                      className="sns-btn bg-white border border-[var(--border)] px-4 py-2 text-xs inline-flex items-center gap-1.5"
                      data-testid={`lead-reply-${lead.id}`}
                    >
                      <Mail size={14} /> {isES ? "Responder por email" : "Reply by email"}
                    </a>
                    <button
                      onClick={() => setStatus(lead.id, "contacted")}
                      disabled={status === "contacted"}
                      className="sns-btn bg-amber-50 text-amber-700 hover:bg-amber-100 px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`lead-set-contacted-${lead.id}`}
                    >
                      <CheckCheck size={14} /> {isES ? "Marcar contactado" : "Mark contacted"}
                    </button>
                    <button
                      onClick={() => setStatus(lead.id, "converted")}
                      disabled={status === "converted"}
                      className="sns-btn bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`lead-set-converted-${lead.id}`}
                    >
                      <TrendingUp size={14} /> {isES ? "Marcar convertido" : "Mark converted"}
                    </button>
                    <button
                      onClick={() => setStatus(lead.id, "discarded")}
                      disabled={status === "discarded"}
                      className="sns-btn bg-slate-100 text-slate-600 hover:bg-slate-200 px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`lead-set-discarded-${lead.id}`}
                    >
                      <Ban size={14} /> {isES ? "Descartar" : "Discard"}
                    </button>
                    {status !== "new" && (
                      <button
                        onClick={() => setStatus(lead.id, "new")}
                        className="sns-btn bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-2 text-xs inline-flex items-center gap-1.5"
                        data-testid={`lead-reopen-${lead.id}`}
                      >
                        <Sparkles size={14} /> {isES ? "Reabrir" : "Reopen"}
                      </button>
                    )}
                    <button
                      onClick={() => removeLead(lead.id)}
                      className="sns-btn bg-red-50 text-red-700 hover:bg-red-100 px-3 py-2 text-xs inline-flex items-center gap-1.5 ml-auto"
                      data-testid={`lead-delete-${lead.id}`}
                    >
                      <Trash2 size={14} /> {isES ? "Eliminar" : "Delete"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {convertLead && (
        <ConvertLeadModal
          lead={convertLead}
          isES={isES}
          onClose={() => setConvertLead(null)}
          onSuccess={async (result) => {
            setConvertResult(result);
            setConvertLead(null);
            await reload();
          }}
        />
      )}

      {convertResult && (
        <ConversionSuccessModal
          result={convertResult}
          isES={isES}
          onClose={() => setConvertResult(null)}
        />
      )}

      {newLeadOpen && (
        <NewLeadModal
          isES={isES}
          onClose={() => setNewLeadOpen(false)}
          onCreated={async () => {
            setNewLeadOpen(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

/* ============ Convert Lead Modal ============ */
function ConvertLeadModal({ lead, isES, onClose, onSuccess }) {
  const [tenantName, setTenantName] = useState(lead.company || lead.name || "");
  const [adminName, setAdminName] = useState(lead.name || "");
  const [adminEmail, setAdminEmail] = useState(lead.email || "");
  const [plan, setPlan] = useState("free");
  const [customPassword, setCustomPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/convert`, {
        tenant_name: tenantName.trim(),
        admin_name: adminName.trim(),
        admin_email: adminEmail.trim(),
        plan,
        custom_password: customPassword.trim() || null,
        lang: isES ? "es" : "en",
      });
      onSuccess(data);
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="convert-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#0F2A4D] to-[#060E1E] text-white p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <UserPlus size={20} />
            </div>
            <div>
              <div className="text-xs text-cyan-300 uppercase tracking-widest font-bold">{isES ? "Nuevo cliente" : "New customer"}</div>
              <h3 className="text-lg font-bold">{isES ? "Convertir lead en cliente" : "Convert lead to customer"}</h3>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-700">
              {isES ? "Nombre del workspace/empresa" : "Workspace / company name"} <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              required
              minLength={2}
              className="sns-input"
              placeholder={isES ? "Ej: Villas Palma S.L." : "E.g. Villas Palma Ltd."}
              data-testid="convert-tenant-name"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-700">
                {isES ? "Nombre del admin" : "Admin name"} <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="text"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                required
                className="sns-input"
                data-testid="convert-admin-name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-700">
                Email <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                className="sns-input"
                data-testid="convert-admin-email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-700">
              {isES ? "Plan inicial" : "Initial plan"}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {["free", "pro"].map((p) => (
                <button
                  type="button"
                  key={p}
                  onClick={() => setPlan(p)}
                  className={`py-2.5 px-3 rounded-xl text-sm font-bold border-2 transition-all ${
                    plan === p ? "border-[var(--coral)] bg-[var(--coral)]/5 text-[var(--coral)]" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                  data-testid={`convert-plan-${p}`}
                >
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-700">
              {isES ? "Contraseña temporal" : "Temporary password"}
              <span className="text-slate-400 font-normal ml-2 text-xs">
                {isES ? "(déjalo vacío para autogenerar)" : "(leave empty to auto-generate)"}
              </span>
            </label>
            <input
              type="text"
              value={customPassword}
              onChange={(e) => setCustomPassword(e.target.value)}
              minLength={6}
              className="sns-input font-mono"
              placeholder={isES ? "Auto (10 caracteres)" : "Auto (10 chars)"}
              data-testid="convert-password"
            />
          </div>

          <div className="p-3 bg-blue-50 rounded-xl text-xs text-blue-700 border border-blue-100">
            💡 {isES
              ? "Se creará un workspace aislado con sus propios clientes, empleados y datos. Se enviará un email con las credenciales."
              : "An isolated workspace will be created with its own clients, employees and data. Credentials will be emailed."}
          </div>

          {err && (
            <div className="p-3 bg-red-50 rounded-xl text-sm text-red-600 border border-red-100" data-testid="convert-error">
              {err}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="sns-btn sns-btn-ghost bg-slate-100 flex-1 py-2.5 text-sm"
                    data-testid="convert-cancel">
              {isES ? "Cancelar" : "Cancel"}
            </button>
            <button type="submit" disabled={loading}
                    className="sns-btn sns-btn-accent flex-1 py-2.5 text-sm disabled:opacity-60"
                    data-testid="convert-submit">
              {loading ? (isES ? "Creando..." : "Creating...") : (isES ? "Crear cliente" : "Create customer")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============ Success Modal ============ */
function ConversionSuccessModal({ result, isES, onClose }) {
  const copy = (text) => {
    navigator.clipboard.writeText(text);
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="conversion-success">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20 mb-3">
            <Check size={28} strokeWidth={3} />
          </div>
          <h3 className="text-xl font-bold">{isES ? "¡Cliente creado!" : "Customer created!"}</h3>
          <p className="text-white/80 text-sm mt-1">{result.tenant_name}</p>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-slate-600 text-sm">
            {isES
              ? "El workspace está listo. Ya se ha enviado un email con las credenciales al cliente. Aquí tienes una copia por si acaso:"
              : "The workspace is ready. Credentials have been emailed to the customer. Here's a copy just in case:"}
          </p>
          <div className="bg-slate-50 rounded-xl p-4 space-y-3 font-mono text-sm">
            <Row label="Email" value={result.admin_email} onCopy={copy} />
            <Row label={isES ? "Contraseña" : "Password"} value={result.temporary_password} onCopy={copy} highlight />
            <Row label="Plan" value={result.plan.toUpperCase()} onCopy={copy} />
          </div>
          <button onClick={onClose}
                  className="sns-btn sns-btn-accent w-full py-2.5 text-sm"
                  data-testid="conversion-success-close">
            {isES ? "Entendido" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, onCopy, highlight }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div>
        <div className="text-[10px] uppercase text-slate-400 font-bold tracking-widest">{label}</div>
        <div className={`text-slate-800 ${highlight ? "font-black text-base" : ""}`}>{value}</div>
      </div>
      <button onClick={() => onCopy(value)}
              className="p-2 rounded-lg text-slate-500 hover:bg-white hover:text-[var(--coral)] transition-colors"
              title="Copiar">
        <Copy size={14} />
      </button>
    </div>
  );
}

/* ============ New Lead Modal (manual entry) ============ */
function NewLeadModal({ isES, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [source, setSource] = useState("whatsapp");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/admin/leads", {
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        phone: phone.trim(),
        message: message.trim(),
        lang: isES ? "es" : "en",
        source,
      });
      onCreated();
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally { setLoading(false); }
  };

  const SOURCES = [
    { id: "whatsapp",   label: "WhatsApp" },
    { id: "phone",      label: isES ? "Llamada" : "Phone call" },
    { id: "referral",   label: isES ? "Recomendación" : "Referral" },
    { id: "in-person",  label: isES ? "En persona" : "In person" },
    { id: "other",      label: isES ? "Otro" : "Other" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="new-lead-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto">
        <div className="bg-gradient-to-br from-[#0F2A4D] to-[#060E1E] text-white p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Plus size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs text-cyan-300 uppercase tracking-widest font-bold">
                {isES ? "Añadir manualmente" : "Add manually"}
              </div>
              <h3 className="text-lg font-bold">{isES ? "Nuevo lead" : "New lead"}</h3>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-700">
                {isES ? "Nombre" : "Name"} <span className="text-[var(--coral)]">*</span>
              </label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     required minLength={1} className="sns-input"
                     data-testid="new-lead-name"
                     placeholder={isES ? "Nombre y apellidos" : "Full name"} />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-700">
                Email <span className="text-[var(--coral)]">*</span>
              </label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required className="sns-input"
                     data-testid="new-lead-email"
                     placeholder="ejemplo@empresa.com" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-700">
                {isES ? "Empresa" : "Company"}
              </label>
              <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
                     className="sns-input" data-testid="new-lead-company"
                     placeholder={isES ? "Opcional" : "Optional"} />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-700">
                {isES ? "Teléfono" : "Phone"}
              </label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                     className="sns-input" data-testid="new-lead-phone"
                     placeholder="+34 600 000 000" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-700">
              {isES ? "¿Cómo te ha llegado?" : "How did they reach out?"}
            </label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {SOURCES.map((s) => (
                <button type="button" key={s.id} onClick={() => setSource(s.id)}
                        className={`py-2 px-2 rounded-xl text-xs font-bold border-2 transition-all ${
                          source === s.id ? "border-[var(--coral)] bg-[var(--coral)]/5 text-[var(--coral)]" : "border-slate-200 text-slate-500 hover:border-slate-300"
                        }`}
                        data-testid={`new-lead-source-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-700">
              {isES ? "Notas / mensaje" : "Notes / message"}
            </label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                      className="sns-input min-h-[100px] resize-y" maxLength={4000}
                      data-testid="new-lead-message"
                      placeholder={isES ? "Ej: llamó preguntando por precios para 5 villas" : "E.g. called asking about pricing for 5 villas"} />
          </div>

          {err && (
            <div className="p-3 bg-red-50 rounded-xl text-sm text-red-600 border border-red-100" data-testid="new-lead-error">
              {err}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="sns-btn sns-btn-ghost bg-slate-100 flex-1 py-2.5 text-sm"
                    data-testid="new-lead-cancel">
              {isES ? "Cancelar" : "Cancel"}
            </button>
            <button type="submit" disabled={loading || !name || !email}
                    className="sns-btn sns-btn-accent flex-1 py-2.5 text-sm disabled:opacity-60"
                    data-testid="new-lead-submit">
              {loading ? (isES ? "Guardando..." : "Saving...") : (isES ? "Crear lead" : "Create lead")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
